// formatMinorUnits renders a Stripe minor-unit amount in its currency. The
// fraction digits come from Intl so zero-decimal currencies (JPY, KRW) are not
// mis-divided by 100 — a ¥925 price is 925 minor units, not 92500.
export function formatMinorUnits(amountMinor: number, currency: string): string {
  const code = (currency || 'usd').toUpperCase();
  const fractionDigits = fractionDigitsFor(code);
  const major = (amountMinor || 0) / 10 ** fractionDigits;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(major);
  } catch {
    return `${code} ${major.toFixed(fractionDigits)}`;
  }
}

// fractionDigitsFor is the currency's minor-unit exponent (2 for USD/EUR/INR, 0
// for JPY/KRW), derived from Intl. Falls back to 2 for an unknown code.
function fractionDigitsFor(code: string): number {
  try {
    return (
      new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

// isZeroDecimal reports whether the currency has no minor unit (JPY, KRW, …) —
// its amounts are whole units, so an amount input steps by 1, not 0.01.
export function isZeroDecimal(currency: string): boolean {
  return fractionDigitsFor((currency || 'usd').toUpperCase()) === 0;
}

// minorToMajorText renders a minor-unit amount as a plain editable number string
// (no symbol/grouping) for an amount input — whole units for zero-decimal
// currencies.
export function minorToMajorText(amountMinor: number, currency: string): string {
  const fractionDigits = fractionDigitsFor((currency || 'usd').toUpperCase());
  return (amountMinor / 10 ** fractionDigits).toString();
}

// majorToMinor parses an amount input's text into minor units for the currency,
// or null for blank/invalid/negative input. Zero-decimal currencies take whole
// units (no ×100).
export function majorToMinor(text: string, currency: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  const fractionDigits = fractionDigitsFor((currency || 'usd').toUpperCase());
  return Math.round(value * 10 ** fractionDigits);
}
